<script lang="ts">
  import { t } from '$core/i18n';
  import {
    primaryLabel,
    secondaryLabel,
    type SessionStore
  } from '$features/session';
  import { Badge } from '$ui/primitives/Badge';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let { store }: { store: SessionStore } = $props();

  let totalItems = $derived(
    store.reviewGroups.reduce((acc, g) => acc + g.items.length, 0)
  );

  let counterText = $derived.by(() => {
    const g = store.reviewGroups.length;
    const i = totalItems;
    const groupLabel = g === 1 ? t('modeB.groupSingular') : t('modeB.groupPlural');
    const itemLabel = i === 1 ? t('modeB.itemSingular') : t('modeB.itemPlural');
    return `${g} ${groupLabel} · ${i} ${itemLabel}`;
  });
</script>

<div class="review-groups">
  <header class="head">
    <p class="hint">{t('modeB.groupsHint')}</p>
    <span class="counter">{counterText}</span>
  </header>

  {#if store.reviewLoading}
    <div class="skeleton-list" role="status" aria-label={t('modeB.groupsTitle')}>
      {#each Array(3) as _, i (i)}
        <div class="skeleton-row"></div>
      {/each}
    </div>
  {:else if store.reviewGroups.length === 0}
    <EmptyState
      icon="check-circle"
      title={t('modeB.groupsEmpty')}
      description={t('modeB.groupsEmptyHint')}
    />
  {:else}
    <ul class="group-list">
      {#each store.reviewGroups as g (g.key)}
        <li>
          <button
            type="button"
            class="group-card"
            data-urgent={g.key === store.mostUrgentGroupKey || undefined}
            data-active={store.selectedGroupKey === g.key || undefined}
            onclick={() => store.selectGroup(g.key)}
          >
            <span class="group-texts">
              <span class="group-title">{primaryLabel(g)}</span>
              {#if secondaryLabel(g)}
                <span class="group-subtitle">{secondaryLabel(g)}</span>
              {/if}
            </span>
            <span class="group-meta">
              {#if g.key === store.mostUrgentGroupKey}
                <Badge variant="warning" shape="square" size="sm">
                  ⚡ {t('modeB.mostUrgent')}
                </Badge>
              {/if}
              <Badge variant="accent" shape="pill" size="sm">
                {String(g.items.length)}
              </Badge>
            </span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .review-groups {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    width: 100%;
  }

  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .counter {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    white-space: nowrap;
  }

  .group-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .group-card {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    min-height: 56px;
    text-align: left;
    cursor: pointer;
    color: var(--color-text-primary);
    background: var(--color-surface-sunken);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    transition:
      border-color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out),
      transform var(--dur-instant) var(--ease-out);
  }

  .group-card:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-border-strong);
  }

  .group-card:active {
    transform: translateY(1px);
  }

  .group-card[data-urgent] {
    border-color: var(--color-accent);
    border-left-width: 3px;
  }

  .group-card[data-active] {
    border-color: var(--color-brand);
    background: var(--color-surface-hover);
    box-shadow: inset 0 0 0 1px var(--color-brand);
  }

  .group-card:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .group-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex: 0 0 auto;
  }

  .group-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .group-title {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow-wrap: anywhere;
  }

  .group-subtitle {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    overflow-wrap: anywhere;
  }

  /* Skeleton (F6 §58, F9 §7.2). */
  .skeleton-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .skeleton-row {
    height: 56px;
    border-radius: var(--radius-md);
    background: linear-gradient(
      90deg,
      var(--color-surface) 0%,
      var(--color-surface-hover) 50%,
      var(--color-surface) 100%
    );
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton-row { animation: none; opacity: 0.5; }
  }
</style>